import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FileText, Search, Users, X } from 'lucide-react';
import { posApi, reportsApi } from '@/services/api';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import ReportExportButton from '@/components/reports/ReportExportButton';
import Pagination, { LIST_PAGE_SIZE } from '@/components/common/Pagination';
import { downloadCsv } from '@/utils/csv';
import { formatMoney } from '@/utils/currency';

interface ReportCustomer {
  name: string;
  customer_name: string;
  mobile?: string;
}

interface ReceiptRow {
  receipt_no: string;
  date: string;
  customer: string;
  customer_name: string;
  invoice: string;
  payment_method: string;
  amount: number;
  user: string;
}

interface PaymentTotal {
  payment_method: string;
  amount: number;
}

export default function RecoveryReceiptsSection() {
  const { t } = useTranslation();
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const [cashier, setCashier] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedCustomerLabel, setSelectedCustomerLabel] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);

  const { data: cashiers = [] } = useQuery({
    queryKey: ['reportCashiers'],
    queryFn: () => reportsApi.getReportCashiers(),
  });

  const { data: paymentMethods = [] } = useQuery({
    queryKey: ['reportPaymentMethods'],
    queryFn: () => reportsApi.getReportPaymentMethods(),
  });

  const { data: customersRes } = useQuery({
    queryKey: ['customersForRecoveryReceipts', customerSearch],
    queryFn: async () => {
      const res = await posApi.getCustomers({ search: customerSearch, limit: 50 });
      return res.data.message;
    },
  });

  const customers: ReportCustomer[] = customersRes?.customers || customersRes || [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target as Node)) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectCustomer = (customer?: ReportCustomer) => {
    if (!customer) {
      setSelectedCustomer('');
      setSelectedCustomerLabel('');
    } else {
      setSelectedCustomer(customer.name);
      setSelectedCustomerLabel(
        customer.mobile ? `${customer.customer_name} - ${customer.mobile}` : customer.customer_name
      );
    }
    setCustomerSearch('');
    setShowCustomerDropdown(false);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['recoveryReceipts', from_date, to_date, selectedCustomer, paymentMethod, cashier],
    queryFn: () =>
      reportsApi.getRecoveryReceipts({
        from_date,
        to_date,
        customer: selectedCustomer || undefined,
        payment_method: paymentMethod || undefined,
        cashier: cashier || undefined,
      }),
  });

  const receipts: ReceiptRow[] = data?.receipts || [];
  const paymentTotals: PaymentTotal[] = data?.payment_totals || [];

  useEffect(() => {
    setPage(0);
  }, [receipts, from_date, to_date, selectedCustomer, paymentMethod, cashier]);

  const handleExport = () => {
    if (!receipts.length) return;
    downloadCsv(
      `recovery-receipts-${from_date}-${to_date}.csv`,
      [
        { key: 'receipt_no', label: 'Receipt No' },
        { key: 'date', label: 'Date' },
        { key: 'customer_name', label: 'Customer' },
        { key: 'invoice', label: 'Invoice' },
        { key: 'payment_method', label: 'Payment Method' },
        { key: 'amount', label: 'Amount' },
        { key: 'user', label: 'User' },
      ],
      receipts as unknown as Record<string, unknown>[]
    );
  };

  const pageRows = receipts.slice(page * LIST_PAGE_SIZE, (page + 1) * LIST_PAGE_SIZE);

  return (
    <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/60 border border-slate-100 overflow-hidden">
      <div className="p-5 border-b border-slate-100 space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <ReportDateRangeFilter
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            customFrom={customFrom}
            customTo={customTo}
            onCustomFromChange={setCustomFrom}
            onCustomToChange={setCustomTo}
          />

          <div className="space-y-1.5 flex-1 min-w-[200px] sm:min-w-[240px] relative" ref={customerDropdownRef}>
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              Customer
            </label>
            {selectedCustomer ? (
              <div className="input-field flex items-center justify-between gap-2 py-2">
                <span className="truncate text-sm text-gray-900">{selectedCustomerLabel}</span>
                <button
                  type="button"
                  onClick={() => handleSelectCustomer()}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  aria-label="Clear customer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    className="input-field pl-9"
                    placeholder={t('reports.searchByNamePhone')}
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setShowCustomerDropdown(true);
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                  />
                </div>
                {showCustomerDropdown && (
                  <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                    <button
                      type="button"
                      onClick={() => handleSelectCustomer()}
                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-100 text-gray-700"
                    >
                      All Customers
                    </button>
                    {customers.length > 0 ? (
                      customers.map((c) => (
                        <button
                          key={c.name}
                          type="button"
                          onClick={() => handleSelectCustomer(c)}
                          className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50"
                        >
                          <p className="font-medium text-gray-900">{c.customer_name}</p>
                          {c.mobile && <p className="text-xs text-gray-500">{c.mobile}</p>}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-4 text-sm text-gray-400 text-center">No customers found</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="space-y-1.5 flex-1 min-w-[160px]">
            <label className="text-sm font-medium text-gray-700">Payment Method</label>
            <select
              className="input-field"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <option value="">All Methods</option>
              {(paymentMethods as string[]).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5 flex-1 min-w-[160px]">
            <label className="text-sm font-medium text-gray-700">Cashier</label>
            <select className="input-field" value={cashier} onChange={(e) => setCashier(e.target.value)}>
              <option value="">All Cashiers</option>
              {(cashiers as { user: string; full_name: string }[]).map((c) => (
                <option key={c.user} value={c.user}>
                  {c.full_name || c.user}
                </option>
              ))}
            </select>
          </div>
          <ReportExportButton onClick={handleExport} disabled={!receipts.length} />
        </div>
      </div>

      {paymentTotals.length > 0 && (
        <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 border-b border-gray-100">
          {paymentTotals.map((t) => (
            <div key={t.payment_method} className="rounded-lg border border-gray-100 bg-gray-50/80 p-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t.payment_method}</p>
              <p className="mt-1 text-base font-semibold text-gray-900">{formatMoney(t.amount)}</p>
            </div>
          ))}
        </div>
      )}

      <div className="overflow-x-auto max-h-[420px]">
        {isLoading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : receipts.length > 0 ? (
          <table className="w-full text-sm text-left min-w-[800px]">
            <thead className="text-xs font-bold text-[#1fa8ff] bg-white uppercase sticky top-0 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 font-medium">Receipt No</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium">Invoice</th>
                <th className="px-4 py-3 font-medium">Payment Method</th>
                <th className="px-4 py-3 font-medium text-right">Amount</th>
                <th className="px-4 py-3 font-medium">User</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pageRows.map((row) => (
                <tr key={row.receipt_no} className="hover:bg-gray-50/50">
                  <td className="px-4 py-3 whitespace-nowrap text-gray-900">{row.receipt_no}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                    {row.date ? format(new Date(row.date), 'd MMM yyyy') : ''}
                  </td>
                  <td className="px-4 py-3 text-gray-900">{row.customer_name}</td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{row.invoice}</td>
                  <td className="px-4 py-3 text-gray-700">{row.payment_method}</td>
                  <td className="px-4 py-3 text-right font-medium">{formatMoney(row.amount)}</td>
                  <td className="px-4 py-3 text-gray-500">{row.user}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-12 text-center">
            <FileText className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No receipts found for the selected filters</p>
          </div>
        )}
      </div>

      {receipts.length > 0 && (
        <div className="p-4 border-t border-gray-100 bg-gray-50">
          <Pagination page={page} pageSize={LIST_PAGE_SIZE} total={receipts.length} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
